import { useMemo } from 'react';

import {
  CodingStrategyEnum,
  OrientationEnum,
  PaperFormatEnum,
} from '@e-socna/data-layer';
import { EMPTY_ARRAY, EMPTY_STRING } from '@e-socna/empty';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { useIdFactory } from '../../../hooks/useIdFactory.hook';
import { useEditableTrackableData } from '../../../hooks/useEditableTrackableData.hook';

import type { SectionTemplatePage_OneSectionTemplate } from '../../SectionTemplate/hooks/useFindOneSectionTemplateQuery.hook';

export const useInitialValuesFactory = () => {
  const { generateId } = useIdFactory();
  const { trackableData } = useEditableTrackableData();

  const {
    genericStatusesCodeMap,
    sectionTypesCodeMap,
    contentOriginsCodeMap,
    codingStrategiesCodeMap,
    printStylesCodeMap,
  } = useReferencesContext();

  const initialValues: SectionTemplatePage_OneSectionTemplate = useMemo(
    () => ({
      __typename: 'SectionTemplate' as const,
      _id: generateId(),
      codingConfig: {
        __typename: CodingStrategyEnum.CodingConfigManual as const,
        _id: generateId(),
        kind: CodingStrategyEnum.CodingConfigManual,
        strategyId: codingStrategiesCodeMap.CodingConfigManual._id,
        code: EMPTY_STRING,
      },
      printing: {
        __typename: 'SectionTemplatePrinting' as const,
        _id: generateId(),
        pdfPrints: EMPTY_ARRAY,
        pdfPrintsIncrement: 0,
        printSettings: {
          __typename: 'PrintSettings' as const,
          _id: generateId(),
          addFooters: true,
          addHeaders: true,
          printStyleId: printStylesCodeMap.Blank._id,
        },
      },
      code: EMPTY_STRING,
      label: EMPTY_STRING,
      typeId: sectionTypesCodeMap.Normal._id,
      statusId: genericStatusesCodeMap.Enabled._id,
      virtualRoot: {
        __typename: 'Section' as const,
        _id: generateId(),
        isVirtual: true,
        contentOriginId: contentOriginsCodeMap.Special._id,
        subSections: EMPTY_ARRAY,
        orientation: OrientationEnum.Portrait,
        paperFormat: PaperFormatEnum.A4,
        hasPagebreakAfter: false,
        hasPagebreakBefore: false,
      },
      ...trackableData,
    }),
    [
      codingStrategiesCodeMap.CodingConfigManual._id,
      contentOriginsCodeMap.Special._id,
      generateId,
      genericStatusesCodeMap.Enabled._id,
      printStylesCodeMap.Blank._id,
      sectionTypesCodeMap.Normal._id,
      trackableData,
    ],
  );

  return { initialValues };
};
